'use client';

import React, { useRef, useState } from 'react';

export interface CardCarouselProps extends React.HTMLAttributes<HTMLElement> {
  as: React.ElementType;
  count: number;
}

// Mobile swipe track for <CardGroup isCarousel>. The swiping itself is CSS
// scroll-snap (see .card-group--carousel in cardGroup.scss); this only keeps
// the dot paging in sync with the scroll position and scrolls on dot click.
// From Tablet up the track is the normal grid and the dots are hidden.
const CardCarousel: React.FC<CardCarouselProps> = ({
  as: Component,
  count,
  children,
  ...props
}) => {
  const trackRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);

  // One card is the track's full width, plus the gap between cards.
  const stepOf = (track: HTMLElement) =>
    track.clientWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || !track.clientWidth) return;
    setActive(Math.round(track.scrollLeft / stepOf(track)));
  };

  const goTo = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * stepOf(track), behavior: 'smooth' });
    setActive(index);
  };

  return (
    <div className="card-group-carousel">
      <Component {...props} ref={trackRef} onScroll={handleScroll}>
        {children}
      </Component>
      <div className="card-group-carousel__dots">
        {Array.from({ length: count }, (_, index) => (
          <button
            key={index}
            type="button"
            className="card-group-carousel__dot"
            aria-label={`Show card ${index + 1} of ${count}`}
            aria-current={index === active ? 'true' : undefined}
            onClick={() => goTo(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default CardCarousel;
